import Vue from "vue";
import { DateHelper } from "../../core/helpers/dateHelper";
import { Format } from "../../core/helpers/formatHelper";
import { BookingTeam, TeamPosition } from "@shared/core/constants";

interface IBookingParticipant {
  Id: number;
  Position: TeamPosition;
  Team: BookingTeam;
  Logo: string;
  CustomerCompleteName: string;
  BookingId: number;
}

type Team = [IBookingParticipant | null, IBookingParticipant | null];

interface IBookingTeams {
  team1: Team;
  team2: Team;
}

class BookingTeamsBuilder {
  private readonly team1: Team;
  private readonly team2: Team;

  constructor(
    private readonly teamsConfig: {
      maxParticipantsPerTeam: number /*, numberOfTeams: number */;
    },
    private readonly bookingId: number,
    private readonly vue: Vue
  ) {
    if (this.teamsConfig.maxParticipantsPerTeam > 2) {
      throw new Error("Only 2 participants per team are allowed");
    }
    const emptyPlayerTeam1 = this.getEmptyPlayer(BookingTeam.Team1);
    const emptyPlayerTeam2 = this.getEmptyPlayer(BookingTeam.Team2);
    this.team1 = [emptyPlayerTeam1, emptyPlayerTeam1];
    this.team2 = [emptyPlayerTeam2, emptyPlayerTeam2];
  }

  public build(participants: IBookingParticipant[]) {
    try {
      this.addParticipants(participants);
    } catch (e) {
      console.log(e);
    } finally {
      this.clearEmptySpots();
    }
  }

  /**
   * Returns team1 participants sorted from left to right
   * and team2 participants sorted from right to left
   * for them to be painted properly
   */
  public sortedByPosition(): IBookingTeams {
    return {
      team1: this.team1.filter((x) => x) as Team, // Filter removes null participants
      team2: this.team2.filter((x) => x).reverse() as Team, // Filter removes null participants
    };
  }

  private clearEmptySpots() {
    if (this.teamsConfig.maxParticipantsPerTeam) {
      let emptyIndex = this.team1.findIndex((p) => p != null && p.Id == -1);
      this.team1[emptyIndex] = null;
      emptyIndex = this.team2.findIndex((p) => p != null && p.Id == -1);
      this.team2[emptyIndex] = null;
    }
  }

  private addParticipants(participants: IBookingParticipant[]) {
    participants
      .sort((a, b) => {
        const teamSortValue = b.Team - a.Team; // No-team participants are placed the last
        if (teamSortValue != 0) {
          return teamSortValue;
        }
        return b.Position - a.Position; // Not-positioned participants are placed the last
      })
      .forEach(this.addParticipant.bind(this));
  }

  private getEmptyPlayer(
    team = BookingTeam.NotSet,
    position = TeamPosition.NotSet
  ): IBookingParticipant {
    return {
      CustomerCompleteName: this.vue.$t("Free Spot").toString(),
      Logo: "empty",
      Id: -1,
      BookingId: this.bookingId,
      Team: team,
      Position: position,
    };
  }

  private addParticipant(participant: IBookingParticipant) {
    switch (participant.Team) {
      case BookingTeam.NotSet:
        this.addNoTeamParticipant(participant);
        break;
      default:
        this.addParticipantWithTeam(participant);
        break;
    }
  }

  private addParticipantWithTeam(participant: IBookingParticipant) {
    if (participant.Team == BookingTeam.NotSet) {
      throw new Error("Participant should have a team");
    }
    const team: Team | undefined = this.getTeamTuple(participant.Team);
    if (!team) {
      throw new Error(`No team was found for participant ${participant.Id}`);
    }
    if (
      team.filter((p) => p!.Id !== -1).length >=
      this.teamsConfig.maxParticipantsPerTeam
    ) {
      throw new Error(`${BookingTeam[participant.Team]} is full`);
    }
    if (participant.Position == TeamPosition.NotSet) {
      const emptySpotIndex = team.findIndex((p) => p!.Id === -1);
      if (emptySpotIndex != -1) {
        team[emptySpotIndex] = participant;
        return;
      }
      throw new Error(
        `No position was found for participant ${participant.Id} in ${
          BookingTeam[participant.Team]
        }`
      );
    }
    team[participant.Position - 1] = participant;
  }

  private addNoTeamParticipant(participant: IBookingParticipant) {
    if (participant.Team != BookingTeam.NotSet) {
      throw new Error("Participant should have no team");
    }

    if (participant.Position == TeamPosition.NotSet) {
      // No team and no position are set, find them a place in any team
      if (
        this.team1.filter((p) => p!.Id !== -1).length <
        this.teamsConfig.maxParticipantsPerTeam
      ) {
        const emptySpotIndex = this.team1.findIndex((p) => p!.Id === -1);
        if (emptySpotIndex != -1) {
          this.team1[emptySpotIndex] = participant;
          return;
        }
      }
      if (
        this.team2.filter((p) => p!.Id !== -1).length <
        this.teamsConfig.maxParticipantsPerTeam
      ) {
        const emptySpotIndex = this.team2.findIndex((p) => p!.Id === -1);
        if (emptySpotIndex != -1) {
          this.team2[emptySpotIndex] = participant;
          return;
        }
      }
      throw new Error("Cannot add more participants to any team");
    }

    if (this.team1[participant.Position - 1]!.Id === -1) {
      this.team1[participant.Position - 1] = participant;
      return;
    }
    if (this.team2[participant.Position - 1]!.Id === -1) {
      this.team2[participant.Position - 1] = participant;
      return;
    }
    throw new Error("Cannot add more participants to any team");
  }

  private getTeamTuple(team: BookingTeam) {
    switch (team) {
      case BookingTeam.Team1:
        return this.team1;
      case BookingTeam.Team2:
        return this.team2;
    }
  }
}

export class BookingsHelper {
  static getCancelRefundText(vue: Vue, target: any) {
    if (target === null) {
      return;
    }

    const totalPaid = target.saleTotalPaid || target.totalPaid;
    if (totalPaid) {
      return vue.$t("Cancel booking, debts and refund payments");
    }
    return vue.$t("Cancel booking and debts");
  }

  static getOccupationGroupInfo(group: any, bookingBlocks: any[]) {
    const res: any = {
      resourceGroupId: group.resourceGroup.id,
      resourceGroupName: group.resourceGroup.name,
      meanByResource: 0,
      occupation: 0,
      totalHours: 0,
      participants: 0,
    };

    if (Format.IsNull(group) || Format.IsNull(group.resources)) {
      return null;
    }

    //If there is a resource with allowOverlaps and maxOverlaps is null, then hide occupation info
    if (
      group.resources.find(
        (x) =>
          (x.overlaps.allowOverlaps || x.overlaps.allowOverlapsInAdmin) &&
          Format.IsNull(x.overlaps.maxOverlaps)
      ) !== undefined
    ) {
      return null;
    }

    const totalResources = group.resources.length;
    let openHours = 0;
    group.resources.forEach((y) => {
      const dtStart = DateHelper.toSystemDate(y.dtStart) as Date;
      const dtEnd = DateHelper.toSystemDate(y.dtEnd) as Date;
      let resourceDuration =
        (dtEnd.valueOf() - dtStart.valueOf()) / 1000 / 60 / 60;
      if (y.overlaps.allowOverlaps || y.overlaps.allowOverlapsInAdmin) {
        resourceDuration = y.overlaps.maxOverlaps * resourceDuration;
      }
      openHours += resourceDuration;
    });

    bookingBlocks.forEach((x) => {
      const resource = group.resources.find(
        (y) => y.resourceId === x.resourceId
      );
      if (Format.IsNull(resource)) {
        return;
      }

      const endDate = DateHelper.toSystemDate(x.endDate) as Date;
      const startDate = DateHelper.toSystemDate(x.startDate) as Date;
      res.totalHours +=
        (endDate.valueOf() - startDate.valueOf()) / 1000 / 60 / 60;
      res.participants += x.participants.length;
    });

    res.meanByResource =
      totalResources === 0 ? 0 : res.totalHours / totalResources;
    res.occupation = openHours === 0 ? 0 : (100 * res.totalHours) / openHours;
    return res;
  }

  static getTeams(
    maxParticipantsPerTeam: number,
    participants: IBookingParticipant[],
    bookingId: number,
    vue: Vue
  ) {
    const result = new BookingTeamsBuilder(
      { maxParticipantsPerTeam },
      bookingId,
      vue
    );
    try {
      result.build(participants);
    } catch (e) {
      console.error("Error building teams from participants:", e);
    }
    return result.sortedByPosition();
  }

  static getShareInfo(
    isMatch: boolean,
    tenantName: string,
    tenantAddress: string,
    url: string,
    externalUrl: string,
    bookingId: number,
    maxParticipants: number,
    participants: any,
    start: string,
    end: string,
    duration: number | null,
    minLevel: number | null,
    maxLevel: number | null,
    resourceName: string,
    vueinstance: any
  ) {
    let level = "";
    if (minLevel !== null && maxLevel !== null) {
      level = `${vueinstance.$t("Level")} ${minLevel.toFixed(
        2
      )} - ${maxLevel.toFixed(2)}`;
    }

    const players = isMatch
      ? BookingsHelper.getMatchPlayersText(
          bookingId,
          maxParticipants,
          participants,
          vueinstance
        )
      : BookingsHelper.getPlayersText(
          participants,
          maxParticipants,
          vueinstance
        );

    const title = isMatch
      ? vueinstance.$t("Match in")
      : vueinstance.$t("Hop in at");

    const meetingInfo = `📢 ${title} ${tenantName}
${level != "" ? "📈 " + level : ""}
🗓️ ${BookingsHelper.getDateAndDurationTxt(start, end, duration, vueinstance)}
📍 ${tenantAddress}
${resourceName != "" ? "🔵" + resourceName : ""}
${players}

${
  !Format.IsNull(externalUrl)
    ? vueinstance.$t("Join from taykus") + "\n" + externalUrl
    : ""
}

${vueinstance.$t("Join from") + " " + tenantName}
${window.location.origin + url}
`;
    this.copyToClipboard(meetingInfo);
    return meetingInfo;
  }

  static getPlayersText(partis: any, maxParticipants: number, vue: any) {
    if (typeof partis === "string") {
      partis = JSON.parse(partis);
    }

    let text = ``;

    for (const parti of partis) {
      text += ` 🆗 ${parti.Name}  \n`;
    }

    const freeSpots = maxParticipants - partis.length;
    if (freeSpots >= 1) {
      text += ` ❔ ${freeSpots} ${vue.$t("free spots")}`;
    }
    return text;
  }

  static getDateAndDurationTxt(
    start: string,
    end: string,
    duration: number | null,
    vue: any
  ) {
    const strDate = DateHelper.toCultureString(start);
    if (Format.IsNull(duration)) {
      duration = DateHelper.getMinutesDifference(
        DateHelper.toDate(start),
        DateHelper.toDate(end)
      );
    }

    return `${strDate} ${duration} ${vue.$t("mins")}`;
  }

  static getMatchPlayersText(
    bookingId: number,
    maxParticipants: number,
    partis: any,
    vue: any
  ) {
    if (typeof partis === "string") {
      partis = JSON.parse(partis);
    }

    const maxPartis = maxParticipants / 2;
    const res: any = BookingsHelper.getTeams(maxPartis, partis, bookingId, vue);

    let text = ``;
    for (const team1 of res.team1) {
      if (team1.Id === 0) {
        text += ` ❔ ${vue.$t("Free spot")} \n`;
      } else {
        text += ` 🆗 ${team1.CustomerCompleteName ?? team1.Name} (${
          Format.IsNull(team1.Level) ? 0 : team1.Level
        }) \n`;
      }
    }
    text += ` VS \n`;
    for (const team2 of res.team2) {
      if (team2.Id === 0) {
        text += ` ❔ ${vue.$t("Free spot")} \n`;
      } else {
        text += ` 🆗 ${team2.CustomerCompleteName ?? team2.Name} (${
          Format.IsNull(team2.Level) ? 0 : team2.Level
        }) \n`;
      }
    }

    return text;
  }

  static copyToClipboard(text) {
    console.log(text);
    if (!navigator.clipboard) {
      console.log(
        "Yours browser does not allow copy to clipboard" + " " + text
      );
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(function () {
        console.log("Copied to clipboard");
      })
      .catch(function (err) {
        console.error("Unable to copy", err);
      });
  }
}
